import cn from "classnames"
import { useEffect } from "react"

import { Badge, Flex } from "@/atoms"
import { useFilters, useStateFilter } from "@/hooks/useFilters"
import { useLogger } from "@/hooks/useLogger"
import { DropdownFilter } from "../DropdownFilter"
import { RadioFilterItem } from "./RadioFilterItem"

import styles from "./RadioFilter.module.scss"

export interface RadioFilterItemProps {
  key: string
  title: string
  Icon: React.FC
  options: Array<{
    label: string
    value: string
    displayLabel: boolean
  }>
  order?: number
  description?: string
}

export interface RadioFilterProps {
  title: string
  items: RadioFilterItemProps[]
  name: string
  placement: string
  isLoading?: boolean
  addCounter?: boolean
  allowApply?: boolean
  onApply?: (params: URLSearchParams) => void
}

export const RadioFilter = (props: RadioFilterProps) => {
  const {
    title,
    items,
    name,
    placement,
    onApply,
    isLoading = false,
    addCounter = false
  } = props
  const { searchParams, setSearchParams } = useFilters()
  const { logger, EVENTS } = useLogger()

  const createInitialValues = (queryString = searchParams) =>
    items.reduce(
      (acc, { key }) => {
        acc[key] = queryString.get(key)
        return acc
      },
      {} as Record<string, any>
    )
  const onChangeState = (
    prev: Record<string, any>,
    state: Record<string, any>
  ) => {
    const next = new URLSearchParams(prev)

    menuItems.forEach(({ key }) => {
      const value = state[key]

      if (value) {
        next.set(key, `${value}`)
      } else {
        next.delete(key)
      }
    })

    return next
  }

  const { filterState, reset, onFilterStateChange, onClear, onOk } =
    useStateFilter(createInitialValues(), {
      onChangeState,
      setSearchParams
    })

  const menuItems = items.map(({ key, description, ...props }) => ({
    key,
    className: cn(styles.menuItem, filterState.values[key] && styles.withValue),
    label: (
      <Flex vertical gap={8}>
        <RadioFilterItem
          name={key}
          value={filterState.values[key]}
          onChange={(value) => {
            onFilterStateChange(value)

            const key = Object.keys(value)[0]

            logger.info(EVENTS.ANALYTIC_EVENTS.TAB.FILTER.QUICK_FILTERS, {
              name,
              placement,
              key,
              value: value[key]
            })
          }}
          {...props}
        />
        {description && (
          <span className={styles.description}>{description}</span>
        )}
      </Flex>
    )
  }))

  const count = Object.values(createInitialValues(searchParams)).filter(
    Boolean
  ).length

  useEffect(() => {
    const result = menuItems.some(({ key }) => {
      const checked = filterState.values[key]
      const checkedInUrl = searchParams.get(key)

      return checked !== checkedInUrl
    })

    if (result) {
      reset(createInitialValues())
    }

    onOk()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  return (
    <DropdownFilter
      allowApply={props.allowApply}
      disabled={isLoading}
      changed={filterState.changed}
      menuItems={menuItems}
      isPlaceholder={!count}
      okDisabled={!filterState.dirty}
      clearDisabled={!filterState.allowClear}
      okHide={!onApply}
      onOk={() => onApply?.(onChangeState(searchParams, filterState.values))}
      onOpenChange={(open: boolean) => {
        if (open) {
          reset(createInitialValues(), false)
        }
      }}
      onClear={onClear}
    >
      {title}
      {addCounter && !!count && (
        <Badge
          count={count}
          className={cn(styles.badge, isLoading && styles.disabled)}
        />
      )}
    </DropdownFilter>
  )
}
